import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Minus, Plus, ArrowLeft } from 'lucide-react';
import Header from './components/Header10';
import Footer from './components/Footer10';

const ProductDetail10 = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);

    const product = {
        id: id,
        name: 'Handwoven Cotton Saree',
        price: 4990,
        description: 'A beautifully handwoven cotton saree crafted by artisans in West Bengal. The delicate weave and natural dyes create a piece that is both timeless and contemporary. Light enough for everyday wear, elegant enough for special occasions.',
        details: [
            'Handwoven on traditional looms',
            '100% organic cotton',
            'Natural vegetable dyes',
            'Includes matching blouse piece',
            'Dry clean recommended',
        ],
        images: [
            'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['S', 'M', 'L', 'XL'],
        inStock: true,
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

            <div style={{ fontFamily: "'Inter', sans-serif" }} className="min-h-screen bg-white">
                <Header />

                <main className="pt-16 md:pt-[72px]">
                    {/* Back Button */}
                    <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-4 md:py-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-[#78716C] text-xs tracking-wider uppercase hover:text-[#1C1917] transition-colors duration-300 bg-transparent border-none cursor-pointer p-0"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Back
                        </button>
                    </div>

                    <div className="max-w-[1200px] mx-auto px-5 md:px-8 pb-16 md:pb-24">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
                            {/* Images */}
                            <div>
                                <div className="aspect-[3/4] overflow-hidden bg-[#FAFAF9] mb-3">
                                    <img
                                        src={product.images[selectedImage]}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    {product.images.map((img, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImage(index)}
                                            className={`w-16 h-20 overflow-hidden bg-[#FAFAF9] cursor-pointer transition-opacity duration-300 border-none p-0 ${
                                                selectedImage === index ? 'opacity-100' : 'opacity-50 hover:opacity-75'
                                            }`}
                                            style={{
                                                borderBottom: selectedImage === index ? '1px solid #78716C' : '1px solid transparent',
                                            }}
                                        >
                                            <img src={img} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Product Info */}
                            <div className="md:py-4">
                                <p className="text-[#A8A29E] text-xs tracking-[2px] uppercase mb-3">STHIR Collection</p>
                                <h1 className="text-2xl md:text-3xl font-light text-[#1C1917] mb-4">{product.name}</h1>
                                <p className="text-[#78716C] text-lg font-light mb-8">
                                    ₹{product.price.toLocaleString('en-IN')}
                                </p>

                                <div className="w-full h-px bg-[#E7E5E4] mb-8" />

                                <p className="text-[#57534E] text-sm font-light leading-relaxed mb-8">
                                    {product.description}
                                </p>

                                {/* Size Selection */}
                                <div className="mb-8">
                                    <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-3">Size</p>
                                    <div className="flex gap-3">
                                        {product.sizes.map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => setSelectedSize(size)}
                                                className={`w-10 h-10 flex items-center justify-center text-xs font-light cursor-pointer transition-all duration-300 ${
                                                    selectedSize === size
                                                        ? 'text-[#1C1917] bg-transparent'
                                                        : 'text-[#A8A29E] bg-transparent hover:text-[#78716C]'
                                                }`}
                                                style={{
                                                    border: selectedSize === size
                                                        ? '1px solid #1C1917'
                                                        : '1px solid #E7E5E4',
                                                }}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Quantity */}
                                <div className="mb-8">
                                    <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-3">Quantity</p>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            className="w-8 h-8 flex items-center justify-center text-[#78716C] hover:text-[#1C1917] transition-colors duration-300 bg-transparent cursor-pointer"
                                            style={{ border: '1px solid #E7E5E4' }}
                                        >
                                            <Minus className="w-3 h-3" strokeWidth={1.5} />
                                        </button>
                                        <span className="text-[#1C1917] text-sm font-light w-8 text-center">{quantity}</span>
                                        <button
                                            onClick={() => setQuantity(quantity + 1)}
                                            className="w-8 h-8 flex items-center justify-center text-[#78716C] hover:text-[#1C1917] transition-colors duration-300 bg-transparent cursor-pointer"
                                            style={{ border: '1px solid #E7E5E4' }}
                                        >
                                            <Plus className="w-3 h-3" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>

                                {/* Add to Cart */}
                                <button
                                    className="w-full py-3.5 bg-[#78716C] text-white text-xs tracking-[2px] uppercase font-normal hover:bg-[#57534E] transition-colors duration-300 cursor-pointer border-none mb-4"
                                >
                                    Add to Cart
                                </button>

                                <div className="w-full h-px bg-[#E7E5E4] my-8" />

                                {/* Product Details */}
                                <div>
                                    <p className="text-[#1C1917] text-xs tracking-[2px] uppercase mb-4">Details</p>
                                    <ul className="space-y-3">
                                        {product.details.map((detail, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <div className="w-1 h-1 bg-[#78716C] rounded-full mt-1.5 flex-shrink-0" />
                                                <span className="text-[#57534E] text-sm font-light">{detail}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default ProductDetail10;
